import {MaybeRefOrGetter, onScopeDispose, readonly, ref, toValue, watch} from "vue";

export interface UseBarcodeScannerOptions {
    /** Called with the decoded value once a burst is recognised. */
    onScan: (code: string) => void;
    /** Listen only while this is true — typically a dialog open state. */
    enabled?: MaybeRefOrGetter<boolean>;
    /** Shortest accepted value, to ignore stray keystrokes. */
    minLength?: number;
    /** Above this gap between two keys, the buffer is a human typing. */
    interKeyThresholdMs?: number;
    /** Normalisation applied before handing the value over. */
    transform?: (raw: string) => string;
}

/**
 * Read a barcode reader plugged in keyboard-wedge mode.
 *
 * Such a reader types its payload as an ordinary keystroke burst ending with
 * Enter, far faster than a human. We buffer printable keys and only treat the
 * result as a scan when the whole burst came in under the inter-key
 * threshold — so somebody typing the code by hand is never mistaken for a
 * scan, and both input paths can coexist on the same screen.
 */
export default function useBarcodeScanner(options: UseBarcodeScannerOptions) {
    const {
        onScan,
        enabled,
        minLength = 6,
        interKeyThresholdMs = 30,
        transform = (raw: string) => raw.trim().toUpperCase(),
    } = options;

    const isListening = ref(false);

    let buffer = '';
    let lastKeyAt = 0;

    const reset = () => {
        buffer = '';
        lastKeyAt = 0;
    };

    const handleKeydown = (event: KeyboardEvent) => {
        // Shortcuts are never part of a scan.
        if (event.ctrlKey || event.metaKey || event.altKey) {
            return;
        }

        const now = event.timeStamp || performance.now();

        if (event.key === 'Enter') {
            const code = transform(buffer);

            reset();

            if (code.length >= minLength) {
                // Swallow the terminator so it cannot submit anything.
                event.preventDefault();
                event.stopPropagation();

                onScan(code);
            }

            return;
        }

        // Only printable single characters belong to a payload.
        if (event.key.length !== 1) {
            return;
        }

        // A slow keystroke means a human: start a fresh buffer from it.
        buffer = lastKeyAt && (now - lastKeyAt) > interKeyThresholdMs
            ? event.key
            : buffer + event.key;

        lastKeyAt = now;
    };

    const start = () => {
        if (isListening.value) {
            return;
        }

        // Capture phase, so a burst is seen even while an input has focus.
        window.addEventListener('keydown', handleKeydown, true);
        isListening.value = true;
    };

    const stop = () => {
        if (!isListening.value) {
            return;
        }

        window.removeEventListener('keydown', handleKeydown, true);
        isListening.value = false;
        reset();
    };

    if (enabled === undefined) {
        start();
    } else {
        watch(() => toValue(enabled), (active) => active ? start() : stop(), {immediate: true});
    }

    onScopeDispose(stop);

    return {
        isListening: readonly(isListening),
        start,
        stop,
    };
}
